import * as React from "react";
import { createRoot } from "react-dom/client";
import {
  applyServerConfiguration,
  getVersionLabelFromServerResponse,
  ServerConfigurationResponse,
  loadRuntimeConfig
}
  from "../configuration";
import api from "../api/api";
import { MsalProvider } from '@azure/msal-react';
import { initializeMsal } from '../auth/msalConfig';
import App from "./components/App";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";

const title = "BuilderMetrix® Task Pane";
const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

function renderConfigurationError() {
  root?.render(
    <div style={{ padding: "24px", fontFamily: "'Segoe UI', sans-serif", color: "#323130", textAlign: "center" }}>
      <h2 style={{ color: "#001C71", fontSize: "16px" }}>Unable to connect to BuilderMetrix®</h2>
      <p style={{ fontSize: "12px", lineHeight: "1.5" }}>
        The add-in could not load its configuration from the server, so sign-in is currently unavailable.
      </p>
      <p style={{ fontSize: "12px", lineHeight: "1.5" }}>
        Please check your network connection and try again. If the problem persists, contact your administrator.
      </p>
      <button
        onClick={() => initializeApp()}
        style={{
          marginTop: "12px",
          padding: "6px 20px",
          borderRadius: "6px",
          border: "0.25pt solid #C4E1E0",
          backgroundColor: "#ffffff",
          color: "#001C71",
          fontSize: "12px",
          cursor: "pointer",
        }}
      >
        Retry
      </button>
    </div>
  );
}

async function initializeApp() {
  try {
    await loadRuntimeConfig();
    Office.onReady(async () => {
      let version = "unknown";
      let isConfigurationLoaded = false;
      try {
        const response = await api.fetch("configuration", "", "");
        if (response.ok) {
          const serverConfig = (await response.json()) as ServerConfigurationResponse;
          if (serverConfig) {
            applyServerConfiguration(serverConfig);
            version = getVersionLabelFromServerResponse(serverConfig);
            isConfigurationLoaded = true;
          }
        } else {
          console.error("Configuration request failed with status:", response.status);
        }
      } catch (error) {
        console.error("Error fetching configuration:", error);
      }

      // Do not mount the app (and its Microsoft/Okta SSO auto-login) without a valid server configuration.
      if (!isConfigurationLoaded) {
        renderConfigurationError();
        return;
      }

      // Has to finish before the render below - see initializeMsal for why.
      const msalInstance = await initializeMsal().catch((error) => {
        console.error("Failed to initialize MSAL:", error);
        return null;
      });
      if (!msalInstance) {
        renderConfigurationError();
        return;
      }

      root?.render(
        <MsalProvider instance={msalInstance}>
          <FluentProvider theme={webLightTheme}>
            <App title={title} version={version} />
          </FluentProvider>
        </MsalProvider>
      );
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error("Failed to initialize application:", errorMessage);
    renderConfigurationError();
  }
}
// Start the app initialization
initializeApp();
if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root?.render(NextApp);
  });
}
